<script setup>
// The lists of Agents, Inboxes, Labels and Teams (R6) on the kit's table: the name links to the detail (with the
// agent's avatar and availability, the inbox's channel icon or the label's colour) and the woot's five numbers.
// Not measured is "-" (7-10: an average with no conversation); a count measured as zero is 0. Loading draws the
// kit's skeleton rows instead of the veil with a spinner; a failed fetch retries once and then alerts, as before.
import OverviewReportFilters from './OverviewReportFilters.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty,
} from 'dashboard/components-next/polaris/ui/table';
import { Card } from 'dashboard/components-next/polaris/ui/card';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import UserAvatar from 'dashboard/components-next/polaris/cards/UserAvatar.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);

const flagMap = {
  agent: 'isFetchingAgentSummaryReports',
  inbox: 'isFetchingInboxSummaryReports',
  team: 'isFetchingTeamSummaryReports',
  label: 'isFetchingLabelSummaryReports',
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(() => uiFlags.value[flagMap[props.type]] ?? false);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id));
const { t } = useI18n();

const nameHeader = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`)
);
const METRIC_HEADERS = [
  'SUMMARY_REPORTS.CONVERSATIONS',
  'SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME',
  'SUMMARY_REPORTS.AVG_RESOLUTION_TIME',
  'SUMMARY_REPORTS.AVG_REPLY_TIME',
  'SUMMARY_REPORTS.RESOLUTION_COUNT',
];

const renderAvgTime = value => (value ? formatTime(value) : '-');
const renderCount = value =>
  value === undefined || value === null ? '-' : value.toLocaleString();

const tableData = computed(() =>
  rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id) || {};
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      thumbnail: row.thumbnail,
      status: row.availability_status,
      color: row.color,
      inbox: props.type === 'inbox' ? row : null,
      metrics: [
        renderCount(conversationsCount),
        renderAvgTime(avgFirstResponseTime),
        renderAvgTime(avgResolutionTime),
        renderAvgTime(avgReplyTime),
        renderCount(resolvedConversationsCount),
      ],
    };
  })
);
const showRoute = computed(() => `${props.type}_reports_show`);

const fetchReportsWithRetry = async () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  try {
    await store.dispatch(props.actionKey, params);
  } catch {
    try {
      await store.dispatch(props.actionKey, params);
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
    }
  }
};

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  fetchReportsWithRetry();
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <OverviewReportFilters
    :disabled="isLoading"
    @filter-change="onFilterChange"
  />
  <Card
    class="min-w-0 gap-0 overflow-hidden py-0"
    :aria-busy="isLoading || undefined"
    data-summary-table
  >
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead class="min-w-48 px-4">{{ nameHeader }}</TableHead>
          <TableHead
            v-for="header in METRIC_HEADERS"
            :key="header"
            class="h-auto whitespace-normal px-4 py-2 text-end"
          >
            {{ t(header) }}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="isLoading">
          <TableRow v-for="n in 5" :key="n">
            <TableCell class="px-4"><Skeleton class="h-6 w-48" /></TableCell>
            <TableCell v-for="c in 5" :key="c" class="px-4">
              <Skeleton class="ms-auto h-5 w-12" />
            </TableCell>
          </TableRow>
        </template>
        <TableEmpty v-else-if="!tableData.length" :colspan="6">
          <span class="text-polaris-muted-foreground">
            {{ t('REPORT.FILTER_ACTIONS.EMPTY_LIST') }}
          </span>
        </TableEmpty>
        <template v-else>
          <TableRow v-for="row in tableData" :key="row.id">
            <TableCell class="max-w-72 px-4">
              <router-link
                :to="{ name: showRoute, params: { id: row.id } }"
                class="flex min-w-0 items-center gap-2 font-medium text-polaris-foreground hover:underline"
              >
                <UserAvatar
                  v-if="type === 'agent'"
                  :name="row.name"
                  :src="row.thumbnail || ''"
                  :status="row.status || ''"
                  :size="24"
                  decorative
                />
                <ChannelIcon
                  v-else-if="row.inbox"
                  :inbox="row.inbox"
                  class="size-4 shrink-0 text-polaris-muted-foreground"
                />
                <span
                  v-else-if="type === 'label'"
                  class="size-2.5 shrink-0 rounded-full"
                  :style="{ backgroundColor: row.color }"
                  aria-hidden="true"
                />
                <span class="truncate">{{ row.name }}</span>
              </router-link>
            </TableCell>
            <TableCell
              v-for="(value, index) in row.metrics"
              :key="index"
              class="px-4 text-end tabular-nums"
              :class="value === '-' ? 'text-polaris-muted-foreground' : ''"
            >
              {{ value }}
            </TableCell>
          </TableRow>
        </template>
      </TableBody>
    </Table>
  </Card>
</template>
